// Workplace by Ricoh — Pitch Deck (V2 SlideDeck template).
//
// A self-contained, typed `SlideDeck` authored against web/src/lib/slide-model.ts
// and the V2 render contract in web/SLIDES.md. British English, tokens only,
// no emoji. Real platform figures are preserved (203 customers / 560 locations /
// 46,178 spaces / 23M bookings, +30% attendance / -20% real-estate / +40%
// satisfaction, the Observe/Operate/Optimise bundles, the CL roadmap). Pricing
// figures are indicative budgetary outputs, not a quote. The customer quote is an
// anonymised composite — replace with a named, consented reference before
// external use.

import type { SlideDeck } from '../slide-model';

export const wbrPitchDeck: SlideDeck = {
  schemaVersion: 2,
  meta: {
    title: 'Workplace by Ricoh',
    subtitle: 'Make the workplace work.',
    theme: 'workplace-paper',
    fontset: 'inter',
    brand: '#d7000f',
  },
  slides: [
    // 1 — Cover / statement
    {
      id: 'wbr-01',
      kind: 'cover',
      label: '01 · Cover',
      layout: 'statement',
      coverStyle: 'statement',
      eyebrow: 'Workplace by Ricoh',
      title: 'The workplace, finally\nworking as one.',
      sub: 'One platform for booking, visitors, issues, occupancy and AI — across every building you run.',
      byline: 'RICOH RDx · Digital Experience',
      foot: 'Confidential · 2026',
    },

    // 2 — Split / image-left — the context, why now
    {
      id: 'wbr-02',
      kind: 'split',
      label: '02 · The problem',
      layout: 'image-left',
      side: 'left',
      art: 'slate',
      eyebrow: 'The problem',
      title: 'Hybrid broke the workplace.',
      lede: 'Offices were designed for full attendance. They are now half-full, unpredictable, and stitched together from a dozen disconnected tools.',
      items: [
        'Desks booked but empty — utilisation guessed, not measured',
        'Visitors logged on paper, reception in chaos',
        'Facilities issues lost in email and sticky notes',
        'Five point solutions, five contracts, no single view',
      ],
      caption: 'The average enterprise runs five or more workplace tools that do not talk to each other.',
    },

    // 3 — Stat-row / centred — the cost of the status quo
    {
      id: 'wbr-03',
      kind: 'stat-row',
      label: '03 · Cost of inaction',
      layout: 'centred',
      title: 'What disconnection costs',
      lede: 'The value organisations leave on the table when the workplace runs blind.',
      stats: [
        { n: '~40%', accent: '40%', label: 'of booked desks sit empty on any given day' },
        { n: '5+', label: 'point tools per enterprise to cover one workplace' },
        { n: '£10M', label: 'lease decisions made on gut feel, not data' },
      ],
      footer: 'Sources: RICOH Spaces occupancy benchmarks; customer discovery.',
    },

    // 4 — Full-bleed / overlay-centre — the shift
    {
      id: 'wbr-04',
      kind: 'full-bleed',
      label: '04 · The shift',
      layout: 'overlay-centre',
      align: 'center',
      art: 'dawn',
      eyebrow: 'The shift',
      title: 'Stop managing tools.\nStart running a workplace.',
      lede: 'Workplace by Ricoh converges the whole workplace into one platform — and puts AI at the centre of it, as a coworker, not a feature.',
    },

    // 5 — Features / trio — what it is, the three principles
    {
      id: 'wbr-05',
      kind: 'features',
      label: '05 · What it is',
      layout: 'trio',
      eyebrow: 'What it is',
      title: 'One platform, three principles',
      cards: [
        {
          icon: 'home',
          h: 'Connected workplace',
          d: 'IoT sensors, room panels, access control and calendars on one data fabric — vendor-agnostic by design.',
        },
        {
          icon: 'trending',
          h: 'Intelligent by default',
          d: 'Occupancy forecasting, ghost-meeting detection and natural-language analytics built in, not bolted on.',
        },
        {
          icon: 'shield',
          h: 'Enterprise-grade',
          d: 'SSO and SAML, AD sync, RBAC, GDPR data residency. Proven multi-region, multi-tenant at 200+ customer scale.',
        },
      ],
    },

    // 6 — Agenda / numbered-list — the apps
    {
      id: 'wbr-06',
      kind: 'agenda',
      label: '06 · The apps',
      layout: 'numbered-list',
      eyebrow: 'The apps',
      title: 'Six apps, one experience',
      items: [
        { t: 'Employee App', n: 'Booking, 3D wayfinding, schedule and the AI assistant — the daily front door for every employee.' },
        { t: 'Visitor Management', n: 'Pre-registration, QR touchless sign-in, badge printing and compliance.' },
        { t: 'Manage', n: 'Operational intelligence — alerting, AI-contextualised issues, building-systems integration.' },
        { t: 'Issue Submission & Service Delivery', n: 'Map-based reporting through to resolution, with external contractor access.' },
        { t: 'Occupancy & Environment', n: 'Real-time and forecast occupancy; CO2, temperature and air-quality monitoring.' },
        { t: 'Workplace AI', n: 'A coworker across booking, issue submission and triage — on web, mobile and room panels.' },
      ],
    },

    // 7 — KPI / grid-2x2 — proof, the platform at scale (REAL figures)
    {
      id: 'wbr-07',
      kind: 'kpi',
      label: '07 · Proof at scale',
      layout: 'grid-2x2',
      eyebrow: 'Proof',
      title: 'Live at scale, today',
      lede: 'Workplace by Ricoh is not a concept. The platform runs production workloads across the EU region now (March 2026).',
      kpis: [
        { label: 'Customers', value: '203', delta: 'EU region', up: true },
        { label: 'Locations', value: '560', delta: 'multi-site', up: true },
        { label: 'Spaces managed', value: '46,178', delta: 'desks & rooms', up: true },
        { label: 'Bookings processed', value: '23M', delta: 'cumulative', up: true },
      ],
      footer: 'Source: RDx data platform, EU region, March 2026.',
    },

    // 8 — Bignum / split-context — the outcome that lands hardest
    {
      id: 'wbr-08',
      kind: 'bignum',
      label: '08 · The outcome',
      layout: 'split-context',
      value: '-20%',
      vlabel: 'real-estate cost, driven by occupancy intelligence and right-sizing',
      delta: '+30% attendance · +40% satisfaction',
      down: false,
      lede: 'Customers using the full platform consistently report a step-change across cost, attendance and experience.',
      footer: 'RICOH Spaces quantified-outcomes benchmark.',
    },

    // 9 — Split / image-right — how it works
    {
      id: 'wbr-09',
      kind: 'split',
      label: '09 · How it works',
      layout: 'image-right',
      side: 'right',
      art: 'mist',
      eyebrow: 'How it works',
      title: 'Three layers, no lock-in',
      lede: 'A shared platform underneath, the capabilities you buy in the middle, and the experiences your people actually touch on top.',
      items: [
        'Platform layer — auth, data, integrations and AI services, shared once',
        'Capability modules — what you buy: booking, occupancy, issues, visitors',
        'Persona endpoints — Employee App, Manage, Visitor App, panels and AI',
      ],
      caption: 'Adopt a module today, add the next when you are ready — same data, same login.',
    },

    // 10 — Compare / two-col — why us vs the alternative
    {
      id: 'wbr-10',
      kind: 'compare',
      label: '10 · The difference',
      layout: 'two-col',
      eyebrow: 'The difference',
      title: 'Platform, not point solutions',
      left: {
        tag: 'Point tools',
        title: 'The usual stack',
        highlight: false,
        items: [
          'Separate booking, visitor and helpdesk vendors',
          'No shared occupancy or analytics layer',
          'Bolt-on reporting, no native 3D maps',
          'Each tool a fresh integration and contract',
        ],
      },
      right: {
        tag: 'Workplace by Ricoh',
        title: 'One platform',
        highlight: true,
        items: [
          'Booking, visitors, issues and occupancy in one',
          'Native 3D indoor mapping and wayfinding',
          'AI and analytics built into every surface',
          'One login, one data model, one vendor',
        ],
      },
    },

    // 11 — Pricing / cards — commercial model (REAL bundle names, indicative figures)
    {
      id: 'wbr-11',
      kind: 'pricing',
      label: '11 · Commercial model',
      layout: 'table',
      eyebrow: 'Commercial model',
      title: 'Three ways to start',
      lede: 'Bundled tiers, priced per building and per asset. Indicative annual figures for a 500-desk, 40-room HQ on a 3-year term — not a quote.',
      tiers: [
        {
          name: 'Observe',
          price: '£17,600',
          period: '/yr indicative',
          note: 'See your workplace clearly',
          tag: 'Entry',
          featured: false,
          features: [
            'Core booking — desk, room, parking',
            'Occupancy & utilisation analytics',
            'Web and mobile apps',
            'SSO, AD sync, RBAC',
          ],
        },
        {
          name: 'Operate',
          price: '£24,100',
          period: '/yr indicative',
          note: 'Run it day to day',
          tag: 'Most popular',
          featured: true,
          features: [
            'Everything in Observe',
            'Visitor Management and Issue Submission',
            '3D indoor mapping and wayfinding',
            'Manage — alerting and AI issue triage',
          ],
        },
        {
          name: 'Optimise',
          price: '£28,900',
          period: '/yr indicative',
          note: 'Make it intelligent',
          tag: 'Full platform',
          featured: false,
          features: [
            'Everything in Operate',
            'Environmental monitoring and forecasting',
            'Workplace AI across all surfaces',
            'Best multi-year and bundle discount',
          ],
        },
      ],
      matrix: [
        { label: 'Booking & occupancy analytics', cells: [true, true, true] },
        { label: 'SSO, AD sync, RBAC', cells: [true, true, true] },
        { label: 'Visitor Management & Issue Submission', cells: [false, true, true] },
        { label: '3D indoor mapping & wayfinding', cells: [false, true, true] },
        { label: 'Workplace AI across all surfaces', cells: [false, false, true] },
        { label: 'Support', cells: ['Standard', 'Priority', 'Dedicated CSM'] },
      ],
      footer: 'Additive margin on FOT. Use VEKTOR Orders for committed pricing.',
    },

    // 12 — Timeline / horizontal — the roadmap (REAL milestones)
    {
      id: 'wbr-12',
      kind: 'timeline',
      label: '12 · Roadmap',
      layout: 'horizontal',
      eyebrow: 'Roadmap',
      title: 'From live product to full catalogue',
      milestones: [
        { date: 'May 2026', t: 'Visitor Management go-live', n: 'Standalone VM in production.', done: true },
        { date: 'Sep 2026', t: 'CL-1 commercial launch', n: 'Issue Management, per-asset booking and Manage SaaS, commercially live.', done: false, active: true },
        { date: 'Nov 2026', t: 'CL-2 full catalogue', n: 'Complete module set launches across the platform.', done: false },
        { date: 'FY27', t: 'CL-3 AI-first', n: 'Workplace AI across every surface; analyst-ready and Magic Quadrant readiness.', done: false },
      ],
      footer: 'Source: RDx commercial-launch plan (CL-1 / CL-2 / CL-3).',
    },

    // 13 — Quote-image / portrait-overlay — customer voice (anonymised composite)
    {
      id: 'wbr-13',
      kind: 'quote-image',
      label: '13 · Customer voice',
      layout: 'portrait-overlay',
      art: 'dusk',
      quote: 'We stopped guessing. For the first time we can see exactly which spaces are used, when, and by whom — and make portfolio decisions on data, not on a hunch.',
      name: 'Real Estate Director',
      meta: 'Multi-site enterprise customer',
    },

    // 14 — Stat-row / row — proof restated as customer outcomes
    {
      id: 'wbr-14',
      kind: 'stat-row',
      label: '14 · Outcomes',
      layout: 'row',
      title: 'What customers get back',
      lede: 'Across professional services, financial services, technology and government.',
      stats: [
        { n: '+30%', accent: '30%', label: 'office attendance' },
        { n: '-50%', accent: '50%', label: 'meeting-room no-shows' },
        { n: '-60%', accent: '60%', label: 'visitor processing time' },
        { n: '60%+', accent: '60%', label: 'employee adoption within three months' },
      ],
    },

    // 15 — CTA / centred — the close
    {
      id: 'wbr-15',
      kind: 'cta',
      label: '15 · Close',
      layout: 'centred',
      eyebrow: 'Make the workplace work',
      headline: 'Start with one module.\nGrow into the platform.',
      sub: 'See your own buildings on Workplace by Ricoh in a 2 to 4 week sandbox POC.',
      contact: [
        ['Web', 'workplacebyricoh.com'],
        ['Demo', 'Sandbox POC'],
        ['Contact', 'RDx · Digital Experience'],
      ],
    },
  ],
  notes: {
    'wbr-01': 'Open on the promise. One platform, one workplace. Land the tagline before anything else.',
    'wbr-02': 'Set the tension. Everyone in the room recognises the disconnected-tools sprawl — name it.',
    'wbr-03': 'Quantify the pain. The ~40% empty-desk figure and the £10M lease decision do the work here.',
    'wbr-04': 'The pivot slide. From managing tools to running a workplace, with AI as a coworker.',
    'wbr-05': 'The three principles, not a feature list. Connected, intelligent, enterprise-grade.',
    'wbr-06': 'Walk the six apps quickly. Manage is the current brand for Mission Control; Workplace AI is cross-cutting.',
    'wbr-07': 'The credibility moment. These are real production figures — slow down and let them land.',
    'wbr-08': 'One outcome that matters most: -20% real-estate cost, with attendance and satisfaction alongside.',
    'wbr-09': 'Architecture in plain terms. Three layers, adopt incrementally, no lock-in.',
    'wbr-10': 'Frame the choice: a stack of point tools versus one platform. The highlighted column is us.',
    'wbr-11': 'Bundle names are real. Stress the figures are indicative for a 500/40 HQ — re-run for the real portfolio.',
    'wbr-12': 'The roadmap shows momentum: live today, commercial launch in September, full catalogue by November.',
    'wbr-13': 'Anonymised composite quote. Swap for a named, consented reference before any external use.',
    'wbr-14': 'Restate the proof as customer outcomes across sectors. Close the value case.',
    'wbr-15': 'The ask: start with one module via a short sandbox POC. Leave them with the next step.',
  },
};
